import { Trans } from 'react-i18next';
import { ReactNode, useEffect, useState } from 'react';
import { useFormContext, UseFormSetValue } from 'react-hook-form';
import Lottie from 'react-lottie';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  Button,
  ShadcnButton,
  Icon,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from '@anytag/shared-atoms';
import { TextareaField, TextField } from '@anytag/shared-molecules';
import { starsImageAnimation } from '@anytag/shared-assets';
import {
  GenerateMarketplaceCampaignDetailStatus,
  GetGeneratedMarketplaceCampaignDetailPayloadGeneratedDetail,
  MarketplaceCampaignStatus,
  useGenerateMarketplaceCampaignDetailMutation,
  useGetGeneratedMarketplaceCampaignDetailQuery
} from '@anytag/graphql';
import { useQueryHelper } from '@anytag/shared-hooks';
import { cn } from '@anytag/shared-utils';
import { MarketplaceFormKeys, MarketplaceFormValues } from '../MarketplaceCampaignForms';
import { AiRainbowSpinner } from './Spinner';
import { DescriptionGeneratorStateReturnType, useValidateDescriptionFields } from './hooks';

interface DescriptionGeneratorProps extends DescriptionGeneratorStateReturnType {
  children?: ReactNode;
}
export const DescriptionGenerator = ({
  loading,
  children,
  progress,
  onClear,
  setStatus,
  onStartLoading,
  setContentForce
}: DescriptionGeneratorProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { watch, setValue } = useFormContext<MarketplaceFormValues>();
  const values = watch();
  const { handleValidateDescriptionInputs, isFormValid, descriptionErrors, resetErrors } =
    useValidateDescriptionFields();

  const [requestId, setRequestId] = useState('');
  const [openSection, setOpenSection] = useState('');
  const [hasGeneratedContent, setHasGeneratedContent] = useState(false);

  const { callGenerateMarketplaceCampaignDetail } = useGenerateMarketplaceCampaignDetailMutation({
    onCompleted: ({ generateMarketplaceCampaignDetail }) => {
      if (generateMarketplaceCampaignDetail) {
        setRequestId(generateMarketplaceCampaignDetail.requestId);
        startPolling(5000); // 5s seems fine now, but we can change if needed
        setStatus('loading');
      }
    }
  });
  const { startPolling, stopPolling, data } = useGetGeneratedMarketplaceCampaignDetailQuery({
    skip: !requestId,
    variables: {
      input: {
        requestId
      }
    },
    onError: (err) => {
      setRequestId('');
      stopPolling();
      setStatus('resolved');
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  });

  useEffect(() => {
    const detailsResponse = data?.getGeneratedMarketplaceCampaignDetail;
    if (detailsResponse) {
      if (detailsResponse.status === GenerateMarketplaceCampaignDetailStatus.SUCCEEDED) {
        stopPolling();
        enqueueSnackbar(t('Campaign description was successfully generated'), { variant: 'success' });
        setRequestId('');
        onClear();
        setStatus('resolved');
        setHasGeneratedContent(true);
        if (detailsResponse.generatedDetail) {
          setMarkdownFieldsAsync(detailsResponse.generatedDetail, setValue, setContentForce);
        }
      } else if (detailsResponse.status === GenerateMarketplaceCampaignDetailStatus.ERROR) {
        stopPolling();
        enqueueSnackbar(t('Sorry, we are experiencing server issues Please try again later'), { variant: 'error' });
        setRequestId('');
        onClear();
        setStatus('resolved');
      }
    }
  }, [
    t,
    onClear,
    setValue,
    setStatus,
    stopPolling,
    setRequestId,
    setContentForce,
    enqueueSnackbar,
    data?.getGeneratedMarketplaceCampaignDetail
  ]);

  const hasSelectedCountry = values.countryId !== '';
  const isSupportedByStatus = [MarketplaceCampaignStatus.REVIEWING].includes(values.status); // initially campaign status is also reviewing, but actually it is draft
  useEffect(() => {
    if (!hasSelectedCountry && isSupportedByStatus && hasGeneratedContent) {
      setRequestId('');
      stopPolling();
      setStatus('idle');
      setOpenSection('');
      setMarkdownFieldsAsync({ requirement: '', serviceInformation: '', title: '' }, setValue, setContentForce);
    }

    return () => {
      setRequestId('');
      stopPolling();
      setStatus('idle');
      setHasGeneratedContent(false);
    };
  }, [hasSelectedCountry, isSupportedByStatus, hasGeneratedContent]);

  const handleStartGeneration = async () => {
    const hasErrors = handleValidateDescriptionInputs(values);

    if (!hasErrors) {
      onStartLoading();
      try {
        await callGenerateMarketplaceCampaignDetail({
          variables: {
            input: {
              countryId: values.countryId,
              campaignType: values.campaignType,
              companyUrl: values.descriptionGenerator_websiteUrl,
              productUrl: values.descriptionGenerator_productUrl,
              prompt: values.descriptionGenerator_additionalInstructions
            }
          }
        });
      } catch (_) {
        onClear();
      }
    }
  };

  return (
    <Accordion
      type="single"
      value={openSection}
      collapsible
      onValueChange={() => setOpenSection(openSection ? '' : 'description-generator')}
    >
      <AccordionItem key="description-generator" value="description-generator" className="last:border-b">
        <AccordionTrigger
          hasIcon={false}
          disabled={!hasSelectedCountry || !isSupportedByStatus}
          className="px-0 hover:bg-white"
        >
          <div className={styles.heading}>
            {children}
            <ShadcnButton
              variant="any-ai-gradient"
              className="flex-none px-2"
              title={
                <div className="flex items-center gap-2">
                  {!hasSelectedCountry || !isSupportedByStatus ? (
                    <Icon icon="ai-icon" size={16} variant="light-gray" />
                  ) : (
                    <Lottie
                      width={16}
                      height={16}
                      style={{
                        margin: 'unset'
                      }}
                      options={{
                        loop: true,
                        autoplay: true,
                        animationData: starsImageAnimation
                      }}
                    />
                  )}
                  {t('Button.Detail Generator')}
                </div>
              }
              {...(!hasSelectedCountry
                ? {
                    disabled: true,
                    tooltip: {
                      help: t<string>('Tooltip.Please select a country first')
                    }
                  }
                : !isSupportedByStatus
                  ? { disabled: true }
                  : {})}
            >
              <div className={openSection ? styles.outlineMask : 'hidden'} />
            </ShadcnButton>
          </div>
        </AccordionTrigger>
        <AccordionContent className={cn('border-t-0 bg-white px-0', { 'm-[1px]': openSection })}>
          <div className={styles.accordionContentBorder}>
            <div className={styles.accordionContent}>
              <div className="flex items-center gap-2">
                <div className={styles.generatorIconBg}>
                  <Icon icon="ai-icon" size={16} variant="white" className="m-auto" />
                </div>
                <span className="text-lg font-semibold">{t('Campaign Detail Generator')}</span>
              </div>
              <p className="text-base text-tertiary">
                <Trans
                  i18nKey={'DescriptionGeneratorInfo.firstRow'}
                  components={{ span: <span className="font-semibold text-primary" /> }}
                />
                <br />
                <Trans
                  i18nKey={'DescriptionGeneratorInfo.secondRow'}
                  components={{ span: <span className="text-primary" /> }}
                />
                <br />
                <Trans i18nKey={'DescriptionGeneratorInfo.thirdRow'} />
              </p>

              <div className="flex gap-3.5">
                <TextField<keyof MarketplaceFormValues>
                  name={MarketplaceFormKeys.descriptionGenerator_productUrl}
                  title={t('TextForm.Product URL')}
                  className="w-76.5 flex-[unset] p-0"
                  placeholder="https://korealcollection.com/collections..."
                  customError={descriptionErrors.productUrl}
                  onChangeCallback={() => resetErrors('productUrl')}
                />
                <TextField
                  name={MarketplaceFormKeys.descriptionGenerator_websiteUrl}
                  title={t('TextForm.Company Website URL')}
                  className="w-76.5 flex-[unset] p-0"
                  placeholder="https://anymindgroup.com/"
                  disabled={!values.descriptionGenerator_productUrl}
                  customError={descriptionErrors.websiteUrl}
                  onChangeCallback={() => resetErrors('websiteUrl')}
                />
              </div>
              <TextareaField
                name={MarketplaceFormKeys.descriptionGenerator_additionalInstructions}
                title={t('TextForm.Additional Instructions')}
                className="p-0"
                placeholder={`• ${t('DescriptionGeneratorInfo.placeholder_1')} \n• ${t('DescriptionGeneratorInfo.placeholder_2')} \n• ${t('DescriptionGeneratorInfo.placeholder_3')}`}
                customError={descriptionErrors.prompt}
                onChangeCallback={() => resetErrors('prompt')}
              />
              <div className="flex justify-end gap-2">
                <Button
                  title={t('Button.Stop')}
                  variant="outline"
                  color="danger"
                  disabled={!loading}
                  onClick={() => {
                    onClear();
                    stopPolling();
                    setStatus('idle');
                  }}
                  className="min-w-23.5"
                />
                <Button
                  title={t('Button.Generate')}
                  variant="solid"
                  className={styles.generateButton}
                  onClick={handleStartGeneration}
                  disabled={
                    !(values.descriptionGenerator_additionalInstructions || values.descriptionGenerator_productUrl) ||
                    !isFormValid
                  }
                  loading={{
                    status: loading,
                    showIcon: true,
                    // @ts-expect-error migrate to TW
                    icon: <AiRainbowSpinner className="left-0" />,
                    title: <span className="w-7.5">{`${progress}%`}</span>
                  }}
                />
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const styles = {
  heading: cn('flex w-full items-center justify-between'),
  accordionContentBorder: cn(
    '--z-index-gradient-border: 0',
    'translate-3d-0 gradient-border-button relative mt-2.5 transform rounded-sm will-change-transform'
  ),
  accordionContent: cn('relative flex flex-col gap-4 p-6', {
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
    padding: '24px',
    position: 'relative'
  }),
  outlineMask: cn(
    'absolute -inset-1 top-0 right-0 bottom-0 left-0 z-[-1] rounded-[inherit] opacity-20',
    'bg-[conic-gradient(from_var(--gradient-angle),#5c5bff_0%,#15d4ff_40%,#ffd235_70%,#ff942e_100%)]'
  ),
  generatorIconBg: cn(
    'flex h-6 w-6',
    'bg-[conic-gradient(from_136deg_at_54.7%_53.44%,#5C5BFF_0deg,#15D4FF_144deg,#FFD235_252deg,#FF942E_360deg)]'
  ),
  generateButton: cn('min-w-23.5', 'disabled:text-black-50 disabled:border-black-30 disabled:bg-black-10')
};

const setMarkdownFieldsAsync = async (
  generatedDetail: GetGeneratedMarketplaceCampaignDetailPayloadGeneratedDetail,
  setValue: UseFormSetValue<MarketplaceFormValues>,
  setContentForce: DescriptionGeneratorStateReturnType['setContentForce']
) => {
  setValue(MarketplaceFormKeys.title, generatedDetail.title);
  // marked.parse will convert .md to html which Suneditor can edit and our MKP campaigns support
  const serviceInformation = DOMPurify.sanitize(await marked.parse(generatedDetail.serviceInformation));
  const requirement = DOMPurify.sanitize(await marked.parse(generatedDetail.requirement));

  setValue(MarketplaceFormKeys.requirement, requirement);
  setValue(MarketplaceFormKeys.serviceInformation, serviceInformation);
  // Note: force update SunEditor content, because we cannot update content directly from props
  setContentForce(requirement, 'postReq');
  setContentForce(serviceInformation, 'serviceInfo');
};
